import { Users } from 'lucide-react';
import { Avatar, cn } from '@cyphex/ui';
import type { MentionTarget } from '../../lib/markdown/mentions';

export interface MentionMenuProps {
  targets: readonly MentionTarget[];
  activeIndex: number;
  onPick: (target: MentionTarget) => void;
  onHover: (index: number) => void;
}

/**
 * The chat composer's `@` popover.
 *
 * Opens UPWARD from the composer's `relative` shell, exactly as the slash menu
 * does: the composer sits at the bottom of the viewport, so a list underneath
 * it would open off-screen. Keyboard ownership stays in the composer - this
 * renders `activeIndex` and reports clicks.
 */
export function MentionMenu({ targets, activeIndex, onPick, onHover }: MentionMenuProps) {
  if (targets.length === 0) return null;

  return (
    <div
      role="listbox"
      aria-label="People to mention"
      className="absolute bottom-full left-0 z-dropdown mb-2 w-full max-w-sm overflow-hidden rounded-lg border border-border bg-popover shadow-dropdown"
    >
      <p className="border-b border-border px-3 py-1.5 text-3xs font-semibold uppercase tracking-wide text-muted-foreground">
        Mention
      </p>
      <ul className="max-h-64 overflow-y-auto py-1">
        {targets.map((target, index) => {
          const active = index === activeIndex;
          return (
            <li key={`${target.kind}:${target.id ?? target.label}`}>
              <button
                type="button"
                role="option"
                aria-selected={active}
                onMouseEnter={() => onHover(index)}
                // `onMouseDown` with preventDefault, never `onClick`: a click
                // blurs the textarea first, and the caret the name has to land
                // at goes with it.
                onMouseDown={(event) => {
                  event.preventDefault();
                  onPick(target);
                }}
                className={cn(
                  'flex w-full items-center gap-2.5 px-3 py-1.5 text-left transition-colors duration-fast',
                  active ? 'bg-primary-subtle' : 'hover:bg-subtle',
                )}
              >
                {target.kind === 'user' ? (
                  <Avatar name={target.label} src={target.avatarUrl ?? undefined} size="sm" />
                ) : (
                  <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-secondary text-muted-foreground">
                    <Users size={14} strokeWidth={2} aria-hidden />
                  </span>
                )}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-md font-medium text-foreground">
                    {target.kind === 'user' ? target.label : `@${target.label}`}
                  </span>
                  {target.hint === undefined ? null : (
                    <span className="block truncate text-sm text-muted-foreground">{target.hint}</span>
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
